import { storeToRefs } from 'pinia';
import useUiStore from '../stores/ui';
import useSettingStore from '../stores/setting';
import useMediaStore from '../stores/media';
import useNavigationTriggerStore from '../stores/navigation-trigger';

interface IframeMessageHandlerOptions {
  onPlayPause: () => void;
  goToNext: () => void;
  goToPrevious: () => void;
}

export const useIframeMessageHandler = (options: IframeMessageHandlerOptions) => {
  const uiStore = useUiStore();
  const { muted, audioVolume } = storeToRefs(useSettingStore());
  const { playing, hasMediaAvailable } = storeToRefs(useMediaStore());
  const { setNavigationTrigger } = useNavigationTriggerStore();

  const {
    toggleDrawerAndControls,
    closeLanguageMenu,
    closeVisibleSettings,
    closeVisiblePlaybackRate,
    closeAnnotationsDrawer,
    toggleTableOfContentDrawer,
    toggleVisibleVolumeSlider,
    closeTableOfContentDrawer,
  } = uiStore;

  const handleIframeMessage = (event: MessageEvent) => {
    const messageHandlers: Record<string, () => void> = {
      'iframe-click': () => {
        toggleDrawerAndControls(true, true);
        closeLanguageMenu();
        closeVisibleSettings();
        closeVisiblePlaybackRate();
        closeAnnotationsDrawer();
      },
      'keyboard-navigation': () => {
        const direction = event.data?.direction;
        if (direction === 'next') {
          setNavigationTrigger('KEYBOARD_RIGHT');
          options.goToNext();
        } else if (direction === 'previous') {
          setNavigationTrigger('KEYBOARD_LEFT');
          options.goToPrevious();
        }
      },
      'keyboard-shortcut': () => {
        const action = event.data?.action;
        if (action === 'toggle-player') {
          // Shift+Cmd/Ctrl+P | Space: Toggle play/pause
          if (!hasMediaAvailable.value) {
            return;
          }
          toggleDrawerAndControls(false, true);
          playing.value = !playing.value;
          options.onPlayPause();
        } else if (action === 'toggle-toc') {
          // Cmd/Ctrl+I: Toggle table of contents
          toggleDrawerAndControls(false, true);
          toggleTableOfContentDrawer(!uiStore.isTableOfContentVisible);
        } else if (action === 'toggle-mute') {
          // M: Toggle mute
          toggleDrawerAndControls(false, true);
          muted.value = !muted.value;
        } else if (action === 'volume-up') {
          // Up Arrow: Increase volume
          toggleDrawerAndControls(false, true);
          toggleVisibleVolumeSlider(true);
          muted.value = false;
          audioVolume.value = Math.min(0.1 + audioVolume.value, 1.0);
        } else if (action === 'volume-down') {
          // Down Arrow: Decrease volume
          toggleDrawerAndControls(false, true);
          toggleVisibleVolumeSlider(true);
          muted.value = false;
          audioVolume.value = Math.max(-0.1 + audioVolume.value, 0.0);
        } else if (action === 'close-overlays') {
          // Escape: Close all overlays
          closeTableOfContentDrawer();
          closeAnnotationsDrawer();
          closeLanguageMenu();
          toggleVisibleVolumeSlider(false);
        }
      },
    };

    const handler = messageHandlers[event.data?.type];
    if (handler) {
      handler();
    }
  };

  return {
    handleIframeMessage,
  };
};
